import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Snackbar, Alert,
    CircularProgress, FormControl, InputLabel, Select, MenuItem, Chip, TablePagination,
} from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon, AccountTree as AccountTreeIcon, Edit as EditIcon, Close as CloseIcon } from "@mui/icons-material";
import LookupService from "../service/LookupService";

const DivisionManagement = () => {
const [divisions, setDivisions] = useState([]);
const [loading, setLoading] = useState(false);
const [saving, setSaving] = useState(false);
const [code, setCode] = useState("");
const [name, setName] = useState("");
const [status, setStatus] = useState("A");
const [editingCode, setEditingCode] = useState(null);
const [tableSearch, setTableSearch] = useState("");
const [statusFilter, setStatusFilter] = useState("all"); // "all" | "A" | "I"
const [page, setPage] = useState(0);
const rowsPerPage = 10;
const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });

const normalize = (item) => ({
code: item.DivisionCode || item.divisionCode || "",
name: item.DivisionName || item.divisionName || "",
status: item.Status || item.status || "A",
});

const loadDivisions = useCallback(async () => {
setLoading(true);
try {
    const data = await LookupService.getDivisions();
    const list = Array.isArray(data) ? data : data?.resultSet || data?.data || [];
    setDivisions(list.map(normalize));
} catch (error) {
    setAlert({ open: true, severity: "error", message: "Failed to load divisions." });
} finally {
    setLoading(false);
}
}, []);

useEffect(() => { loadDivisions(); }, [loadDivisions]);
useEffect(() => { setPage(0); }, [tableSearch, statusFilter]);

const handleSelectRow = (div) => {
    setEditingCode(div.code);
    setCode(div.code);
    setName(div.name);
    setStatus(div.status === "I" ? "I" : "A");
};

const handleCancelEdit = () => {
    setEditingCode(null);
    setCode(""); setName(""); setStatus("A");
};

const handleAdd = async () => {
const trimmedCode = code.trim();
const trimmedName = name.trim();
if (!trimmedCode || !trimmedName) {
    setAlert({ open: true, severity: "error", message: "Both code and name are required." });
    return;
}
const isEditing = editingCode !== null;
setSaving(true);
try {
    const payload = [{ DivisionCode: trimmedCode, DivisionName: trimmedName, Status: status }];
    await LookupService.postDivision(payload);
    setAlert({ open: true, severity: "success", message: isEditing ? "Division updated." : "Division added." });
    setEditingCode(null);
    setCode(""); setName(""); setStatus("A");
    await loadDivisions();
} catch (error) {
    setAlert({ open: true, severity: "error", message: isEditing ? "Failed to update division." : "Failed to add division." });
} finally {
    setSaving(false);
}
};

const filteredDivisions = divisions
    .filter((div) => statusFilter === "all" || div.status === statusFilter)
    .filter((div) =>
        !tableSearch.trim() ||
        div.code.toLowerCase().includes(tableSearch.trim().toLowerCase()) ||
        div.name.toLowerCase().includes(tableSearch.trim().toLowerCase())
    );

return (
<Box sx={{ width: "100%", maxWidth: 800, mx: "auto", mt: 4, px: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
    <AccountTreeIcon sx={{ color: "#142c54", mr: 1 }} />
    <Typography variant="h5" fontWeight={700} color="#142c54">Division</Typography>
    </Box>

    <Paper sx={{ p: 3, mb: 3 }}>
    {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add divisions available for the "Division" dropdown on the Employee Register form.
    </Typography> */}
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField size="small" label="Code" value={code} onChange={(e) => setCode(e.target.value)} sx={{ width: 140 }} disabled={editingCode !== null} />
        <TextField size="small" label="Division name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
        <FormControl size="small" sx={{ width: 130 }}>
        <InputLabel>Status</InputLabel>
        <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="A">Active</MenuItem>
            <MenuItem value="I">Inactive</MenuItem>
        </Select>
        </FormControl>
        <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editingCode !== null ? <EditIcon /> : <AddIcon />)}
            onClick={handleAdd}
            disabled={saving}
            sx={{ backgroundColor: "#142c54", whiteSpace: "nowrap" }}
        >
        {editingCode !== null ? "Update" : "Add"}
        </Button>
        {editingCode !== null && (
        <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelEdit} disabled={saving} sx={{ whiteSpace: "nowrap" }}>
            Cancel
        </Button>
        )}
    </Box>
    </Paper>

    <Paper>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 2, gap: 2, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" color="text.secondary">
        {filteredDivisions.length} of {divisions.length} division{divisions.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="A">Active only</MenuItem>
            <MenuItem value="I">Inactive only</MenuItem>
            </Select>
        </FormControl>
        <TextField
            size="small"
            placeholder="Search by code or name..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            sx={{ width: 220 }}
        />
        <IconButton size="small" onClick={loadDivisions} disabled={loading}>
            <RefreshIcon fontSize="small" />
        </IconButton>
        </Box>
    </Box>
   <TableContainer>
        <Table stickyHeader>
        <TableHead>
            <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Division</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {loading ? (
            <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
            ) : filteredDivisions.length === 0 ? (
            <TableRow><TableCell colSpan={3} align="center" sx={{ color: "text.secondary", py: 4 }}>No divisions found.</TableCell></TableRow>
            ) : (
            filteredDivisions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((div, idx) => (
                <TableRow
                key={`${div.code}-${idx}`}
                hover
                onClick={() => handleSelectRow(div)}
                selected={editingCode === div.code}
                sx={{
                        cursor: "pointer",
                        animation: "rowFade 0.25s ease",
                        "@keyframes rowFade": { from: { opacity: 0 }, to: { opacity: 1 } },
                    }}
                >
                <TableCell>{div.code}</TableCell>
                <TableCell>{div.name}</TableCell>
                <TableCell><Chip size="small" label={div.status === "A" ? "Active" : "Inactive"} color={div.status === "A" ? "success" : "default"} /></TableCell>
                </TableRow>
            ))
            )}
        </TableBody>
        </Table>
    </TableContainer>
    <TablePagination
        component="div"
        count={filteredDivisions.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
    />
    </Paper>

    <Snackbar open={alert.open} autoHideDuration={3000} onClose={() => setAlert((a) => ({ ...a, open: false }))}>
    <Alert severity={alert.severity} onClose={() => setAlert((a) => ({ ...a, open: false }))}>{alert.message}</Alert>
    </Snackbar>
</Box>
);
};

export default DivisionManagement;